import type { FeatureDefinition } from '../definition/FeatureDefinition';
import type { FeaturePrototype } from '../prototype/FeaturePrototype';
import { FeatureEditSession } from './FeatureEditSession';

/**
 * 特征编辑会话管理器。
 */
export class FeatureEditSessionManager {
    /** 活动会话映射。 */
    private readonly sessionByFeatureId = new Map<string, FeatureEditSession>();

    /**
     * 创建或覆盖指定特征的编辑会话。
     *
     * @param prototype 目标特征原型。
     * @param draftDefinition 初始草稿定义。
     * @param editPayload 原始历史、隔离预览历史与影响闭包。
     * @returns 新建的编辑会话。
     */
    public createSession<
        TDefinition extends FeatureDefinition,
        TPrototype extends FeaturePrototype<TDefinition>
    >(
        prototype: TPrototype,
        draftDefinition: TDefinition | undefined,
        editPayload: {
            targetCommand: import('../../../types').CadCommand;
            originalCommands: import('../../../types').CadCommand[];
            previewCommands: import('../../../types').CadCommand[];
            affectedFeatureIds: string[];
        },
    ): FeatureEditSession<TDefinition, TPrototype> {
        const targetCommand = editPayload.targetCommand;
        const session = new FeatureEditSession<TDefinition, TPrototype>({
            sessionId: this.createSessionId(prototype.getFeatureId()),
            prototype,
            targetCommand,
            originalCommands: editPayload.originalCommands,
            previewCommands: editPayload.previewCommands,
            affectedFeatureIds: editPayload.affectedFeatureIds,
            draftDefinition,
        });
        this.sessionByFeatureId.set(prototype.getFeatureId(), session as FeatureEditSession);
        return session;
    }

    /**
     * 读取指定特征的活动会话。
     *
     * @param featureId 特征 ID。
     * @returns 编辑会话；不存在时返回 null。
     */
    public getSession(featureId: string): FeatureEditSession | null {
        return this.sessionByFeatureId.get(featureId) ?? null;
    }

    /**
     * 判断当前是否存在活动特征编辑会话。
     *
     * 编辑会话期间 Worker 历史是"移除目标特征及其下游"的沙箱前缀，不代表文档完整时间线，
     * 调用方据此决定是否允许用 Worker 历史对账完整时间线。
     *
     * @returns 存在任一活动会话时返回 true。
     */
    public hasActiveSession(): boolean {
        return this.sessionByFeatureId.size > 0;
    }

    /**
     * 关闭指定特征的编辑会话。
     *
     * @param featureId 特征 ID。
     */
    public closeSession(featureId: string): void {
        this.sessionByFeatureId.delete(featureId);
    }

    /**
     * 销毁全部活动会话。
     */
    public dispose(): void {
        this.sessionByFeatureId.clear();
    }

    /**
     * 创建稳定的编辑会话 ID。
     *
     * @param featureId 特征 ID。
     * @returns 会话 ID。
     */
    private createSessionId(featureId: string): string {
        return `${featureId}:edit:${Date.now()}`;
    }
}
